import * as analytics from 'analytics/Analytics';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {REVIEWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getUnsubmittedTableNamesForEntry} from 'notebook/reviews/disableAuthorActions';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {GenericResponseJSON} from 'notifications';
import legacyRunModal from 'ui-library/legacyRunModal';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {Review, ReviewAction} from '../ReviewConstants';
import {EntityByStageId, IsTeamReassignableByStageId} from '../ReviewLifecycleSummaryPanel';
import {SendForReviewModal, SendForReviewOnRequestSubmitArgs} from '../SendForReviewModal';
import {OpenedModalSendForReviewAnalyticsMetadata} from './ReviewsAnalyticsUtil';

export function runSendForRLCReviewModal({
  reviewableId,
  existingReviewerUsersByStage,
  onRequestComplete,
  analyticsParams,
  existingCanTeamReassignByStage,
  renderAdditionalReviewInfo,
  renderReviewError,
  performanceMetadata,
  unsubmittedTableNames: externalUnsubmittedTableNames,
}: {
  reviewableId: string;
  existingReviewerUsersByStage?: EntityByStageId;
  onRequestComplete?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  analyticsParams: OpenedModalSendForReviewAnalyticsMetadata;
  existingCanTeamReassignByStage?: IsTeamReassignableByStageId;
  /** Custom rendering of additional info related to the review stage in modal */
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  performanceMetadata?: {[key: string]: string};
  unsubmittedTableNames?: Array<string>;
}): void {
  const errorMessage = ReviewUtil.getReviewBlockingErrorMessage(reviewableId);

  let unsubmittedTableNames: Array<string> | undefined = externalUnsubmittedTableNames;
  if (unsubmittedTableNames === undefined && !BenchlingConfig.ELN_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES) {
    unsubmittedTableNames = getUnsubmittedTableNamesForEntry(reviewableId);
  }

  legacyRunModal(SendForReviewModal, {
    reviewableId,
    onRequestSubmit: async ({comment, stageReviewers}: SendForReviewOnRequestSubmitArgs) => {
      await ReviewUtil.ensureCurrentRuntimeSnapshotPersisted(reviewableId);
      const {review, filePermissions, snapshotTask} = await ReviewDAO.sendForRLCReview({
        fileApiId: reviewableId,
        message: comment,
        rlcStageReviewersData: stageReviewers,
      });
      onRequestComplete?.(reviewableId, review, filePermissions, snapshotTask);
    },
    existingReviewerUsersByStage,
    existingCanTeamReassignByStage,
    defaultComment: ReviewUtil.getDefaultReviewComment(ReviewAction.SEND_FOR_REVIEW, true),
    renderAdditionalReviewInfo,
    renderReviewError,
    errorMessage,
    performanceMetadata,
    unsubmittedTableNames,
  });
  analytics.trackEvent(REVIEWS_ANALYTICS_ACTIONS.OPENED_MODAL_SEND_FOR_REVIEW, analyticsParams);
}
